import React, { useContext } from 'react';
import styled from 'astroturf/react';
import { trackClick } from 'js/utils/trackClick';
import { CiterightContext } from '../CiterightContextProvider';
import { Button } from 'js/design_system/Button';
import { Icon } from 'js/design_system/Icon';
import { Menu } from 'js/design_system/Menu';
import { MenuItem } from 'js/design_system/MenuItem';
import { PopoverMode } from 'js/design_system/Popover';
import { WorkData } from '../types';
import { WorkCardActionButton } from './WorkCard';
import { useCiterightCitations } from '../hooks/useCiterightCitations';
import { exportCitationsAsText, ExportFormat } from '../utils/exportCitations';
import { EXPORT_FORMATS } from '../constants/exportFormats';

interface AddToListButtonProps {
  reference: WorkData;
  size?: 'sm' | 'lg';
  claimId?: string;
  claimText?: string;
  pageContext?: 'mini_report' | 'inline_report' | 'preprint_report';
}

export const AddToListButton: React.FC<AddToListButtonProps> = ({
  reference,
  size = 'sm',
  claimId,
  claimText,
  pageContext,
}) => {
  const context = useContext(CiterightContext);

  const {
    generateBibtexForSavedReferences,
    generateRisForSavedReferences,
    generateCitationsForSavedReferences,
    isLoading: isExporting,
  } = useCiterightCitations();

  if (!context) {
    return null;
  }

  const { addReferenceToList, removeReferenceFromList, isSaved } = context;
  const referenceId = reference.work?.id;
  const isAlreadySaved = referenceId ? isSaved(referenceId) : false;

  const handleClick = () => {
    if (referenceId) {
      if (isAlreadySaved) {
        removeReferenceFromList(referenceId);
      } else {
        trackClick({
          source: 'citeright.reference_save_button',
          entityId: context?.reportId ?? undefined,
          extra: {
            page_context: pageContext,
            suggestion_source: reference.source,
            doi: reference.work?.doi,
            title: reference.work?.title?.slice(0, 40),
            journal: reference.work?.publication?.slice(0, 25),
          },
        });
        addReferenceToList(reference, claimId, claimText);
      }
    }
  };

  const handleSaveAndDownload = async (format: ExportFormat) => {
    if (!referenceId) return;

    // Save to list if not already saved
    if (!isAlreadySaved) {
      trackClick({
        source: 'citeright.reference_save_button',
        entityId: context?.reportId ?? undefined,
        extra: {
          page_context: pageContext,
          doi: reference.work?.doi,
          title: reference.work?.title?.slice(0, 40),
          journal: reference.work?.publication?.slice(0, 25),
        },
      });
      addReferenceToList(reference, claimId, claimText);
    }

    // Track the export action
    trackClick({
      source: 'citeright.references_export',
      entityId: context?.reportId ?? undefined,
      extra: {
        format: format.toLowerCase(),
        reference_count: 1,
        page_context: pageContext,
        suggestion_source: reference.source,
      },
    });

    // Export the single reference
    if (format === 'BibTeX') {
      await generateBibtexForSavedReferences([reference]);
    } else if (format === 'RIS') {
      await generateRisForSavedReferences([reference]);
    } else {
      const citations = await generateCitationsForSavedReferences([reference]);
      exportCitationsAsText(citations, format);
    }
  };

  if (isAlreadySaved) {
    return (
      <SplitButtonGroup>
        <SavedButton
          size={size}
          onClick={handleClick}
          aria-label={`Remove "${reference.work?.title}" from saved references`}
          disabled={isExporting}
        >
          <Icon name="check" size={16} />
          Saved
        </SavedButton>
        <Menu
          mode={PopoverMode.Click}
          placement="bottom-end"
          closeOnClick={true}
          render={() => (
            <div>
              {EXPORT_FORMATS.map(({ format, label }) => (
                <MenuItem
                  key={format}
                  onClick={
                    isExporting
                      ? undefined
                      : () => handleSaveAndDownload(format)
                  }
                >
                  {label}
                </MenuItem>
              ))}
              <DropdownDivider />
              <MenuItem onClick={isExporting ? undefined : handleClick}>
                Remove from list
              </MenuItem>
            </div>
          )}
        >
          <SavedDropdownButton
            size={size}
            icon="arrow_drop_down"
            disabled={isExporting}
            aria-label="Download or remove options"
          />
        </Menu>
      </SplitButtonGroup>
    );
  }

  return (
    <SplitButtonGroup>
      <WorkCardActionButton
        size={size}
        onClick={handleClick}
        aria-label={`Add "${reference.work?.title}" to saved references`}
        disabled={isExporting}
      >
        <Icon name="bookmark_add" size={16} />
        <MobileOnly>Save</MobileOnly>
        <DesktopOnly>Add to paper</DesktopOnly>
      </WorkCardActionButton>
      <Menu
        mode={PopoverMode.Click}
        placement="bottom-end"
        closeOnClick={true}
        render={() => (
          <div>
            {EXPORT_FORMATS.map(({ format, label }) => (
              <MenuItem
                key={format}
                onClick={
                  isExporting ? undefined : () => handleSaveAndDownload(format)
                }
              >
                {label}
              </MenuItem>
            ))}
          </div>
        )}
      >
        <Button
          size={size}
          icon="arrow_drop_down"
          disabled={isExporting}
          aria-label="Save and download options"
        />
      </Menu>
    </SplitButtonGroup>
  );
};

const SavedButton = styled(Button)`
  color: var(--brand-colors-success-500);
  border-color: var(--brand-colors-success-100);
  background-color: var(--brand-colors-success-100);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  height: auto;
  padding: 4px 8px;
  cursor: pointer;

  &:hover {
    background-color: var(--brand-colors-success-100);
    border-color: var(--brand-colors-success-100);
  }
`;

const SavedDropdownButton = styled(Button)`
  color: var(--brand-colors-success-500);
  border-color: var(--brand-colors-success-100);
  background-color: var(--brand-colors-success-100);
  border-left: 1px solid var(--brand-colors-success-100);
  padding: 4px;
  height: auto;

  &:hover {
    background-color: var(--brand-colors-success-100);
    border-color: var(--brand-colors-success-100);
    border-left-color: var(--brand-colors-success-100);
  }
`;

const SplitButtonGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0;

  /* First button (left side) */
  & > button:first-child,
  & > *:first-child button {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  /* Menu wrapper (ensures proper flex alignment) */
  & > *:last-child {
    display: flex;
    align-items: center;
  }

  /* Second button (right side, inside Menu wrapper) */
  & > button:last-child,
  & > *:last-child > button {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-left: 1px solid var(--stroke-light);
    padding: 4px;
    min-width: 24px;
    height: auto;
  }
`;

const DropdownDivider = styled.div`
  height: 1px;
  background-color: var(--stroke-light);
  margin: 4px 0;
`;

const DesktopOnly = styled.span`
  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileOnly = styled.span`
  @media (min-width: 769px) {
    display: none;
  }
`;

export default AddToListButton;
